import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
export type Language = 'en' | 'hu';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  updateTranslation: (key: string, value: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation data
const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.joinUs': 'Join Us',
    'nav.language': 'Language',
    
    // Hero Section
    'hero.companyName': 'commodIT',
    'hero.tagline': 'The pioneers of IT',
    'hero.transformBusiness': 'From strategy to delivery with AI solutions, high-performance websites, IT consulting, software builds, and paid ads.',
    'hero.freeConsultation': 'Free Consultation',
    'hero.viewOurWork': 'View Our Work',
    
    // Services Section
    'services.badge': 'Services',
    'services.title': 'Our',
    'services.titleHighlight': 'Services',
    'services.subtitle': 'Five essential services to plan, build, and grow your digital business',
    'services.aiTitle': 'AI Solutions',
    'services.aiDescription': 'Automate workflows and unlock insights with practical AI. From intelligent chatbots and call agents to predictive analytics and RAG-powered search.',
    'services.webTitle': 'Web + SEO',
    'services.webDescription': 'High-performance websites that load fast, rank, and convert, built with technical SEO and clean design from day one.',
    'services.itTitle': 'IT Consulting',
    'services.itDescription': 'Technology guidance that aligns with your business goals: strategy, cloud, security, AI adoption, and transformation roadmaps.',
    'services.softwareTitle': 'Software Development',
    'services.softwareDescription': 'Custom applications tailored to your use case: scalable, secure, and ready to integrate with your stack.',
    'services.socialTitle': 'Automate Workflows',
    'services.socialDescription': 'Streamline your business processes with intelligent automation solutions that eliminate manual tasks and boost productivity.',
    'services.learnMore': 'Learn More',
    'services.ctaTitle': 'Contact',
    'services.ctaTitleHighlight': 'Us',
    'services.ctaText': 'Ready to transform your business?',
    'services.ctaButton': 'Start Your Project Today',
    
    // Features
    'features.customAI': 'Custom AI Models',
    'features.processAutomation': 'Process Automation',
    'features.chatbots': 'Chatbots (support & sales)',
    'features.analytics': 'Predictive Analytics & forecasting',
    'features.webDesign': 'Modern Web Design',
    'features.seoOptimization': 'SEO Optimization',
    'features.ecommerce': 'E-commerce Solutions',
    'features.performance': 'Performance Optimization',
    'features.techStrategy': 'Technology & product strategy',
    'features.infrastructure': 'Infrastructure Planning',
    'features.security': 'Security Consulting',
    'features.aiConsulting': 'AI Consulting',
    'features.digitalTransformation': 'Digital Transformation',
    'features.customApps': 'Web & mobile apps',
    'features.mobileDev': 'APIs & integrations',
    'features.enterpriseSystems': 'Data pipelines & backends',
    'features.apiIntegration': 'QA automation & CI/CD',
    'features.socialStrategy': 'Workflow Automation',
    'features.contentCreation': 'Task Optimization', 
    'features.adManagement': 'Integration Platforms',
    'features.reporting': 'Process Monitoring',
    
    // About Section
    'about.whyChoose': 'Why Choose',
    'about.commodIT': 'commodIT',
    'about.notJustTech': 'We\'re not just another tech company. We\'re your strategic partner in digital innovation.',
    'about.yourSuccess': 'Your Success is Our Mission',
    'about.buildingPartnerships': 'We\'re a team of software developers and IT consultants. Our approach is personal and pragmatic: understand your goals, deliver quality results, and measure business impact.',
    'about.whatSetsUsApart': 'What Sets Us Apart:',
    'about.yearsExperience': 'Experienced, small team — less overhead costs, more attention',
    'about.clientSatisfaction': 'Transparent process with clear milestones',
    'about.cuttingEdge': 'Modern stack & best practices (performance, security, SEO)',
    'about.multilingualSupport': 'Europe-based, multilingual support (EN/HU), global delivery',
    'about.support247': 'Long-term partnership: maintenance, analytics, and improvements',
    'about.clientSatisfactionBadge': 'Client Satisfaction',
    'about.ourValues': 'Our Values',
    'about.valuesBullets': [
      'Clarity over jargon',
      'Outcome before output',
      'Security by design',
      'Respect for timelines & budgets',
      'Innovation with purpose',
      'Client success first'
    ],
    'about.valuesMotto': 'Building Tomorrow Together',
    'about.valuesMottoDesc': 'Where Technology Meets Trust',
    'about.expertTeam': 'Expert Team',
    'about.expertTeamDesc': 'Seasoned professionals with deep industry knowledge',
    'about.provenResults': 'Proven Results',
    'about.provenResultsDesc': 'Projects focused on measurable outcomes: speed, leads, and efficiency',
    'about.agileDelivery': 'Agile Delivery',
    'about.agileDeliveryDesc': 'Short cycles, clear communication that adapts to your needs',

    // References Section
    'references.our': 'Our',
    'references.works': 'Works',
    'references.portfolioDesc': 'Explore our portfolio of successful projects and digital transformations',
    'references.pauseAutoScroll': 'Pause Auto-scroll',
    'references.playAutoScroll': 'Play Auto-scroll',
    'references.play': 'Play',
    'references.pause': 'Pause',
    'references.tapToView': 'Tap to view',
    'references.view': 'View',
    'references.ecommercePlatform': 'E-Commerce Platform',
    'references.corporateWebsite': 'Corporate Website',
    'references.logisticsDashboard': 'Logistics Dashboard',
    'references.fizgo': 'FIZGO',
    'references.innovationPlatform': 'Innovation Platform',
    'references.mobileApp': 'Mobile Application',
    'references.dataAnalytics': 'Data Analytics Platform',
    'references.aiSolutions': 'AI Solutions',
    'references.webDevelopmentSeo': 'Web Development & SEO',
    'references.softwareDevelopment': 'Software Development',
    'references.softposAutomation': 'softPOS & Automation',
    'references.itConsulting': 'IT Consulting',

    // Join Us Section
    'joinus.joinUs': 'Join Us',
    'joinus.lookingForTalented': 'We\'re looking for talented freelancers to join our growing team. Discover exciting opportunities to work on innovative projects.',
    'joinus.freelanceDeveloper': 'Freelance Low-Code Developer',
    'joinus.developerDesc': 'Build innovative web applications using modern technologies. Work on exciting projects with flexible schedules.',
    'joinus.freelanceAITechnician': 'Freelance AI Technician',
    'joinus.aiTechnicianDesc': 'Implement and maintain AI solutions. Help clients leverage artificial intelligence for their business needs.',
    'joinus.freelanceSalesPerson': 'Freelance Sales Person',
    'joinus.salesPersonDesc': 'Drive business growth by connecting with potential clients. Represent our cutting-edge technology solutions.',
    'joinus.learnMore': 'Learn More',
    'joinus.learnMoreDeveloper': 'Learn More About Developer Position',
    'joinus.learnMoreAITechnician': 'Learn More About AI Technician Position',
    'joinus.learnMoreSales': 'Learn More About Sales Position',

    // Contact Section
    'contact.readyToGetStarted': 'Ready to',
    'contact.getStarted': 'Get Started',
    'contact.discussProject': 'Let\'s discuss your project and explore how we can help transform your business',
    'contact.letsConnect': 'Let\'s Connect',
    'contact.readyToTakeNext': 'Ready to take your business to the next level? Reach out to us for a free consultation. We\'ll analyze your needs and provide tailored recommendations.',
    'contact.email': 'Email',
    'contact.phone': 'Phone',
    'contact.location': 'Location',
    'contact.responseTime': 'Response Time',
    'contact.lessThan24Hours': '< 24 hours',
    'contact.languages': 'Languages',
    'contact.english': 'English',
    'contact.magyar': 'Magyar',
    'contact.emailValue': 'info@commodit.hu',
    'contact.phoneValue': '+36 30 117 6526',
    'contact.freeConsultation': 'Free Consultation',
    'contact.bookDiscovery': 'Book a 30-minute discovery call to discuss your project requirements, challenges, and goals. No commitment required.',
    'contact.projectScope': 'Project scope analysis',
    'contact.techRecommendations': 'Technology recommendations',
    'contact.timelineBudget': 'Timeline & budget estimation',
    'contact.scheduleConsultation': 'Schedule Free Consultation',
    'contact.usuallyResponds': 'Usually responds within 2-4 hours',
    'contact.locationValue': 'Budapest, Hungary',

    // Footer
    'footer.transformingBusinesses': 'Transforming businesses through innovative technology solutions and expert consulting.',
    'footer.services': 'Services',
    'footer.navigation': 'Navigation',
    'footer.legal': 'Legal',
    'footer.privacyPolicy': 'Privacy Policy',
    'footer.downloadPdf': 'Download PDF',
    'footer.allRightsReserved': '© 2025 commodIT. All rights reserved!',
    'footer.aiSolutions': 'AI Solutions',
    'footer.webSeo': 'Web + SEO',
    'footer.itConsulting': 'IT Consulting',
    'footer.softwareDevelopment': 'Software Development',
    'footer.about': 'About',
    'footer.references': 'References',
    'footer.contact': 'Contact',

    // Service Pages - AI Solutions
    'aiSolutions.title': 'AI Solutions',
    'aiSolutions.heroDescription': 'Save your and your company\'s time and money by automating repetitive tasks and workflows. From custom machine learning models to intelligent automation systems that revolutionize how you work.',
    'aiSolutions.startJourney': 'Start Your AI Journey',
    'aiSolutions.whatWeDeliver': 'What We Deliver',
    'aiSolutions.customAIModels': 'Custom AI Models',
    'aiSolutions.customAIDescription': 'Tailor-made machine learning solutions designed specifically for your business needs and data patterns.',
    'aiSolutions.processAutomation': 'Process Automation',
    'aiSolutions.processAutomationDescription': 'Streamline repetitive tasks and workflows with intelligent automation that learns and adapts to your needs.',
    'aiSolutions.intelligentChatbots': 'Intelligent Chatbots',
    'aiSolutions.intelligentChatbotsDescription': 'Advanced conversational AI that provides 24/7 customer support with natural language understanding in both verbal and written form.',
    'aiSolutions.predictiveAnalytics': 'Predictive Analytics',
    'aiSolutions.predictiveAnalyticsDescription': 'Harness historical data to predict future trends and make data-driven business decisions.',
    'aiSolutions.ourAIProcess': 'Our AI Process',
    'aiSolutions.dataAnalysis': 'Data Analysis',
    'aiSolutions.dataAnalysisDescription': 'We analyze your data patterns and business requirements to identify AI opportunities.',
    'aiSolutions.modelDevelopment': 'Model Development',
    'aiSolutions.modelDevelopmentDescription': 'Custom AI models are developed and trained specifically for your use case and data patterns.',
    'aiSolutions.integrationOptimization': 'Integration & Optimization',
    'aiSolutions.integrationOptimizationDescription': 'Seamless integration into your existing systems with continuous optimization.',
    'aiSolutions.readyToHarness': 'Ready to Harness the Power of AI?',
    'aiSolutions.readyToHarnessDescription': 'Let\'s discuss how artificial intelligence can transform your business operations and drive growth.',
    'aiSolutions.getFreeConsultation': 'Get Free AI Consultation',

    // Service Pages - IT Consulting
    'itConsulting.title': 'IT Consulting',
    'itConsulting.heroDescription': 'Strategic technology guidance that improves your business operations. From infrastructure planning to digital transformation, we provide expert consulting that drives real results.',
    'itConsulting.strategicGuidance': 'Get Strategic Guidance',
    'itConsulting.ourExpertise': 'Our Expertise',
    'itConsulting.technologyStrategy': 'Technology Strategy',
    'itConsulting.technologyStrategyDescription': 'Comprehensive technology roadmaps that align with your business goals and drive sustainable growth.',
    'itConsulting.infrastructurePlanning': 'Infrastructure Planning',
    'itConsulting.infrastructurePlanningDescription': 'Scalable IT infrastructure design that grows with your business and optimizes operational efficiency.',
    'itConsulting.securityConsulting': 'Security Consulting',
    'itConsulting.securityConsultingDescription': 'Robust cybersecurity strategies to protect your digital assets and ensure regulatory compliance.',
    'itConsulting.digitalTransformation': 'Digital Transformation',
    'itConsulting.digitalTransformationDescription': 'End-to-end transformation initiatives that modernize your operations and enhance competitiveness.',
    'itConsulting.consultingAreas': 'Consulting Areas',
    'itConsulting.technologyAssessment': 'Technology Assessment',
    'itConsulting.currentStateAnalysis': 'Current State Analysis',
    'itConsulting.gapIdentification': 'Gap Identification',
    'itConsulting.technologyAudit': 'Technology Audit',
    'itConsulting.performanceReview': 'Performance Review',
    'itConsulting.strategicPlanning': 'Strategic Planning',
    'itConsulting.itRoadmapCreation': 'IT Roadmap Creation',
    'itConsulting.budgetPlanning': 'Budget Planning',
    'itConsulting.riskManagement': 'Risk Management',
    'itConsulting.roiAnalysis': 'ROI Analysis',
    'itConsulting.implementationSupport': 'Implementation Support',
    'itConsulting.projectManagement': 'Project Management',
    'itConsulting.changeManagement': 'Change Management',
    'itConsulting.trainingPrograms': 'Training Programs',
    'itConsulting.qualityAssurance': 'Quality Assurance',
    'itConsulting.aiConsulting': 'AI Consulting',
    'itConsulting.aiStrategyDevelopment': 'AI Strategy Development',
    'itConsulting.aiToolSelection': 'AI Tool Selection & Integration',
    'itConsulting.aiWorkflowOptimization': 'AI Workflow Optimization',
    'itConsulting.aiPerformanceMonitoring': 'AI Performance Monitoring',
    'itConsulting.transformTechnologyStrategy': 'Transform Your Technology Strategy',
    'itConsulting.transformTechnologyStrategyDescription': 'Partner with us to align your technology investments with your business objectives and drive sustainable growth.',
    'itConsulting.scheduleStrategySession': 'Schedule Strategy Session',

    // Service Pages - Software Development
    'softwareDevelopment.title': 'Software Development',
    'softwareDevelopment.heroDescription': 'We make custom software solutions tailored to your unique business needs. From mobile apps to enterprise systems, we build scalable, reliable software that drives your business forward.',
    'softwareDevelopment.startProject': 'Start Your Project',
    'softwareDevelopment.whatWeBuild': 'What We Build',
    'softwareDevelopment.customApplications': 'Custom Applications',
    'softwareDevelopment.customApplicationsDescription': 'Software solutions built from the ground up to address your unique business challenges and requirements.',
    'softwareDevelopment.mobileDevelopment': 'Mobile Development',
    'softwareDevelopment.mobileDevelopmentDescription': 'Native and cross-platform mobile apps that deliver exceptional user experiences across iOS and Android.',
    'softwareDevelopment.enterpriseSystems': 'Enterprise Systems',
    'softwareDevelopment.enterpriseSystemsDescription': 'Scalable enterprise-grade software systems that integrate seamlessly with your existing infrastructure.',
    'softwareDevelopment.apiIntegration': 'API Integration',
    'softwareDevelopment.apiIntegrationDescription': 'Seamless integration services that connect your systems and streamline data flow across platforms.',
    'softwareDevelopment.ourTechnologyStack': 'Our Technology Stack',
    'softwareDevelopment.frontend': 'Frontend',
    'softwareDevelopment.backend': 'Backend',
    'softwareDevelopment.mobileCloud': 'Mobile & Cloud',
    'softwareDevelopment.developmentProcess': 'Development Process',
    'softwareDevelopment.discovery': 'Discovery',
    'softwareDevelopment.discoveryDescription': 'Requirements gathering and technical analysis',
    'softwareDevelopment.design': 'Design',
    'softwareDevelopment.designDescription': 'UI/UX design and system architecture',
    'softwareDevelopment.development': 'Development',
    'softwareDevelopment.developmentDescription': 'Agile development with regular updates',
    'softwareDevelopment.testing': 'Testing',
    'softwareDevelopment.testingDescription': 'Comprehensive testing and quality assurance',
    'softwareDevelopment.deployment': 'Deployment',
    'softwareDevelopment.deploymentDescription': 'Launch and ongoing maintenance support',
    'softwareDevelopment.readyToBuild': 'Ready to Build Something Amazing?',
    'softwareDevelopment.readyToBuildDescription': 'Let\'s discuss your software development needs and create a solution that transforms your business operations.',
    'softwareDevelopment.discussProject': 'Discuss Your Project',

    // Service Pages - Automate Workflows
    'automateWorkflows.title': 'Automate Workflows',
    'automateWorkflows.heroDescription': 'Streamline your business processes with intelligent automation solutions that eliminate manual tasks, reduce errors, and boost productivity across your entire organization.',
    'automateWorkflows.startAutomating': 'Start Automating',
    'automateWorkflows.ourServices': 'Our Automation Services',
    'automateWorkflows.workflowAutomation': 'Workflow Automation',
    'automateWorkflows.workflowAutomationDescription': 'Design and implement automated workflows that eliminate repetitive manual tasks and reduce human error.',
    'automateWorkflows.taskOptimization': 'Task Optimization',
    'automateWorkflows.taskOptimizationDescription': 'Optimize task sequences and dependencies to maximize efficiency and minimize processing time.',
    'automateWorkflows.integrationPlatforms': 'Integration Platforms',
    'automateWorkflows.integrationPlatformsDescription': 'Connect and integrate various business systems to create seamless automated processes.',
    'automateWorkflows.processMonitoring': 'Process Monitoring',
    'automateWorkflows.processMonitoringDescription': 'Real-time monitoring and analytics to track workflow performance and identify optimization opportunities.',
    'automateWorkflows.businessAreasTitle': 'Business Areas We Automate',
    'automateWorkflows.customerService': 'Customer Service',
    'automateWorkflows.customerServiceDesc': 'Support ticket routing',
    'automateWorkflows.dataProcessing': 'Data Processing',
    'automateWorkflows.dataProcessingDesc': 'Automated data entry',
    'automateWorkflows.financeAccounting': 'Finance & Accounting',
    'automateWorkflows.financeAccountingDesc': 'Invoice processing',
    'automateWorkflows.marketing': 'Marketing',
    'automateWorkflows.marketingDesc': 'Lead qualification',
    'automateWorkflows.hrOperations': 'HR Operations',
    'automateWorkflows.hrOperationsDesc': 'Employee onboarding',
    'automateWorkflows.inventoryManagement': 'Inventory Management',
    'automateWorkflows.inventoryManagementDesc': 'Stock monitoring',
    'automateWorkflows.qualityAssurance': 'Quality Assurance',
    'automateWorkflows.qualityAssuranceDesc': 'Testing automation',
    'automateWorkflows.communications': 'Communications',
    'automateWorkflows.communicationsDesc': 'Email workflows',
    'automateWorkflows.automationBenefits': 'Automation Benefits',
    'automateWorkflows.increasedEfficiency': 'Increased Efficiency',
    'automateWorkflows.increasedEfficiencyDescription': 'Automate repetitive tasks to free up your team for strategic work and reduce processing time.',
    'automateWorkflows.errorReduction': 'Error Reduction',
    'automateWorkflows.errorReductionDescription': 'Eliminate human errors in routine processes and ensure consistent, accurate results every time.',
    'automateWorkflows.costSavings': 'Cost Savings',
    'automateWorkflows.costSavingsDescription': 'Reduce operational costs through automation while improving service quality and customer satisfaction.',
    'automateWorkflows.twentyFourSevenOperation': '24/7 operation',
    'automateWorkflows.fasterProcessing': 'Faster processing',
    'automateWorkflows.resourceOptimization': 'Resource optimization',
    'automateWorkflows.scalableSolutions': 'Scalable solutions',
    'automateWorkflows.consistentOutput': 'Consistent output',
    'automateWorkflows.dataAccuracy': 'Data accuracy',
    'automateWorkflows.complianceAssurance': 'Compliance assurance',
    'automateWorkflows.qualityControl': 'Quality control',
    'automateWorkflows.lowerLaborCosts': 'Lower labor costs',
    'automateWorkflows.reducedErrors': 'Reduced errors',
    'automateWorkflows.fasterTurnaround': 'Faster turnaround',
    'automateWorkflows.betterResourceAllocation': 'Better resource allocation',
    'automateWorkflows.readyToAutomate': 'Ready to Automate Your Business?',
    'automateWorkflows.readyToAutomateDescription': 'Transform your operations with intelligent automation solutions that work around the clock to boost your productivity and efficiency.',
    'automateWorkflows.startAutomationJourney': 'Start Your Automation Journey',

    // Service Pages - Web & SEO
    'webSeo.title': 'Web & SEO',
    'webSeo.heroDescription': 'Build stunning, high-performance websites that rank at the top of search results. With our approach we ensure your online presence drives real business growth and measurable results.',
    'webSeo.launchWebsite': 'Launch Your Website',
    'webSeo.whatWeCreate': 'What We Create',
    'webSeo.modernWebDesign': 'Modern Web Design',
    'webSeo.modernWebDesignDescription': 'Stunning, responsive websites built with the latest technologies and design principles that captivate your customers.',
    'webSeo.seoOptimization': 'SEO Optimization',
    'webSeo.seoOptimizationDescription': 'Strategic search engine optimization that puts your website at the top of search results and drives organic traffic.',
    'webSeo.ecommerceSolutions': 'E-commerce Solutions',
    'webSeo.ecommerceSolutionsDescription': 'Complete online stores with secure payment processing, inventory management, and conversion optimization.',
    'webSeo.performanceOptimization': 'Performance Optimization',
    'webSeo.performanceOptimizationDescription': 'Lightning-fast loading speeds and optimal user experience that keeps visitors engaged and converting.',
    'webSeo.ourWebProcess': 'Our Web Process',
    'webSeo.strategyPlanning': 'Strategy & Planning',
    'webSeo.strategyPlanningDescription': 'We research your market and create a comprehensive web strategy.',
    'webSeo.designDevelopment': 'Design & Development',
    'webSeo.designDevelopmentDescription': 'Beautiful, responsive designs come to life with modern technology.',
    'webSeo.seoImplementation': 'SEO Implementation',
    'webSeo.seoImplementationDescription': 'Strategic optimization for maximum search engine visibility.',
    'webSeo.launchGrowth': 'Launch & Growth',
    'webSeo.launchGrowthDescription': 'Deployment and ongoing optimization for continuous improvement.',
    'webSeo.readyToDominate': 'Ready to Dominate Search Results?',
    'webSeo.readyToDominateDescription': 'Let\'s create a website that not only looks amazing but also drives traffic and converts visitors into customers.',
    'webSeo.startWebProject': 'Start Your Web Project',

    // Job Pages - Common
    'jobs.backToHome': 'Back to Home',
    'jobs.applyNow': 'Apply Now',
    'jobs.jobDescription': 'Job Description',
    'jobs.keyResponsibilities': 'Key Responsibilities:',
    'jobs.requiredQualifications': 'Required Qualifications',
    'jobs.technicalSkills': 'Technical Skills:',
    'jobs.skills': 'Skills:',
    'jobs.experience': 'Experience:',
    'jobs.whatWeOffer': 'What We Offer',
    'jobs.applyForPosition': 'Apply for This Position',

    // Job Pages - Freelance Developer
    'freelanceDeveloper.title': 'Freelance Low-code Developer',
    'freelanceDeveloper.heroDescription': 'Join our team and build innovative web applications using the newest technologies',
    'freelanceDeveloper.jobDescriptionText': 'We\'re looking for talented freelance developers to join our growing team. You\'ll work on exciting projects for various clients, building modern web applications using mostly low-code / no-code technologies.',
    'freelanceDeveloper.responsibility1': 'Develop responsive web applications in React and TypeScript',
    'freelanceDeveloper.responsibility2': 'Collaborate with design teams to implement user-friendly UIs',
    'freelanceDeveloper.responsibility3': 'Write clean, maintainable, and well-documented modules to complement existing applications',
    'freelanceDeveloper.responsibility4': 'Participate in code reviews and technical discussions',
    'freelanceDeveloper.responsibility5': 'Work with modern development tools and workflows',
    'freelanceDeveloper.experience1': 'Experience in web-development',
    'freelanceDeveloper.experience2': 'Portfolio of completed projects',
    'freelanceDeveloper.experience3': 'Experience with modern automation tools like N8N',
    'freelanceDeveloper.experience4': 'Understanding of responsive design principles',
    'freelanceDeveloper.competitiveRates': 'Competitive Rates',
    'freelanceDeveloper.competitiveRatesDescription': 'Attractive hourly rates based on experience and project complexity',
    'freelanceDeveloper.flexibleSchedule': 'Flexible Schedule',
    'freelanceDeveloper.flexibleScheduleDescription': 'Work on your own schedule with flexible project timelines',
    'freelanceDeveloper.greatTeam': 'Great Team',
    'freelanceDeveloper.greatTeamDescription': 'Collaborate with experienced professionals and grow your skills',
    'freelanceDeveloper.modernTech': 'Modern Tech',
    'freelanceDeveloper.modernTechDescription': 'Work with the latest technologies and best practices',
    'freelanceDeveloper.readyToJoin': 'Ready to Join Our Team?',
    'freelanceDeveloper.readyToJoinDescription': 'Submit your application today and let\'s build amazing projects together. We review all applications carefully and will get back to you within 48 hours.',

    // Job Pages - Freelance AI Technician
    'freelanceAITechnician.title': 'Freelance AI Technician',
    'freelanceAITechnician.heroDescription': 'Help clients implement artificial intelligence for their business needs',
    'freelanceAITechnician.jobDescriptionText': 'Join our AI team as a freelance AI Expert and help businesses transform their operations. You\'ll implement, maintain, and optimize AI solutions for diverse clients.',
    'freelanceAITechnician.responsibility1': 'Implement and deploy AI models and solutions',
    'freelanceAITechnician.responsibility2': 'Integrate AI APIs and services into client applications',
    'freelanceAITechnician.responsibility3': 'Optimize AI workflows for performance and accuracy',
    'freelanceAITechnician.responsibility4': 'Provide technical support and troubleshooting',
    'freelanceAITechnician.responsibility5': 'Document AI implementations and best practices',
    'freelanceAITechnician.experience1': 'Years of AI/ML experience',
    'freelanceAITechnician.experience2': 'Experience with AI model deployment',
    'freelanceAITechnician.experience3': 'Knowledge of cloud AI services (AWS, Azure, GCP)',
    'freelanceAITechnician.experience4': 'Understanding of data processing and analysis',
    'freelanceAITechnician.premiumRates': 'Premium Rates',
    'freelanceAITechnician.premiumRatesDescription': 'High-value projects with competitive compensation for AI expertise',
    'freelanceAITechnician.flexibleWork': 'Flexible Work',
    'freelanceAITechnician.flexibleWorkDescription': 'Remote-first approach with project-based timelines',
    'freelanceAITechnician.expertNetwork': 'Expert Network',
    'freelanceAITechnician.expertNetworkDescription': 'Connect with leading AI professionals and researchers',
    'freelanceAITechnician.cuttingEdgeAI': 'Cutting-Edge AI',
    'freelanceAITechnician.cuttingEdgeAIDescription': 'Work with the latest AI technologies and frameworks',
    'freelanceAITechnician.readyToShape': 'Ready to Shape the Future?',
    'freelanceAITechnician.readyToShapeDescription': 'Join our AI team and help businesses unlock the power of artificial intelligence. Apply today and be part of our AI team.',

    // Job Pages - Freelance Sales Person
    'freelanceSalesPerson.title': 'Freelance Sales Person',
    'freelanceSalesPerson.heroDescription': 'Drive business growth by connecting with potential clients and representing our technology solutions',
    'freelanceSalesPerson.jobDescriptionText': 'Join our sales team and help businesses discover how our technology solutions can transform their operations. You\'ll represent our company, building relationships and driving growth.',
    'freelanceSalesPerson.responsibility1': 'Identify and qualify potential clients and opportunities',
    'freelanceSalesPerson.responsibility2': 'Present our services and solutions to prospects',
    'freelanceSalesPerson.responsibility3': 'Build and maintain strong client relationships',
    'freelanceSalesPerson.responsibility4': 'Negotiate contracts and close deals',
    'freelanceSalesPerson.responsibility5': 'Collaborate with technical teams to deliver solutions',
    'freelanceSalesPerson.experience1': 'Experience B2C and/or B2B sales',
    'freelanceSalesPerson.experience2': 'Experience selling technology solutions',
    'freelanceSalesPerson.experience3': 'Proven track record of meeting targets',
    'freelanceSalesPerson.experience4': 'Strong communication and presentation skills',
    'freelanceSalesPerson.commissionStructure': 'Commission Structure',
    'freelanceSalesPerson.commissionStructureDescription': 'Performance-based commissions',
    'freelanceSalesPerson.flexibleSchedule': 'Flexible Schedule',
    'freelanceSalesPerson.flexibleScheduleDescription': 'Work remotely with flexible hours',
    'freelanceSalesPerson.supportTeam': 'Support Team',
    'freelanceSalesPerson.supportTeamDescription': 'Work with experienced technical support teams',
    'freelanceSalesPerson.growthOpportunities': 'Growth Opportunities',
    'freelanceSalesPerson.growthOpportunitiesDescription': 'Advance your career in a rapidly growing tech company',
    'freelanceSalesPerson.readyToDriveGrowth': 'Ready to Drive Growth?',
    'freelanceSalesPerson.readyToDriveGrowthDescription': 'Join our sales team and help businesses transform with technology. Apply today and start building your success story with us.',

    // Common
    'common.consultation': 'Get Consultation',
    'common.close': 'Close',
    'common.submit': 'Submit',
    'common.loading': 'Loading...',
    
    // Consultation Modal
    'consultation.title': 'Free Consultation Request',
    'consultation.description': 'Tell us about yourself and what you\'d like to discuss. We\'ll get back to you within 24 hours.',
    'consultation.name': 'Name *',
    'consultation.namePlaceholder': 'Your full name',
    'consultation.profession': 'Profession',
    'consultation.professionPlaceholder': 'Your current role',
    'consultation.age': 'Age',
    'consultation.agePlaceholder': 'Your age',
    'consultation.industry': 'Industry',
    'consultation.industryPlaceholder': 'Select your industry',
    'consultation.industryTechnology': 'Technology',
    'consultation.industryHealthcare': 'Healthcare',
    'consultation.industryFinance': 'Finance',
    'consultation.industryEducation': 'Education',
    'consultation.industryRetail': 'Retail',
    
    'consultation.industryManufacturing': 'Manufacturing',
    'consultation.industryConsulting': 'Consulting',
    'consultation.industryStartup': 'Startup',
    'consultation.industryOther': 'Other',
    'consultation.email': 'Email *',
    'consultation.emailPlaceholder': 'your.email@example.com',
    'consultation.phone': 'Phone Number',
    'consultation.phonePlaceholder': '+1 (555) 123-4567',
    'consultation.descriptionLabel': 'What would you like to discuss? *',
    'consultation.descriptionPlaceholder': 'Describe your project, challenges, or what you\'d like to consult about...',
    'consultation.cancel': 'Cancel',
    'consultation.submit': 'Submit Request',
    'consultation.submitting': 'Sending...',
    'consultation.acceptPrivacy': 'I accept the',
    'consultation.privacyPolicy': 'privacy policy',
    'consultation.error': 'Error',
    'consultation.errorDescription': 'Failed to send consultation request. Please try again.',
    
    // Job Application Modal
    'jobApplication.title': 'Apply for {position}',
    'jobApplication.fullName': 'Full Name *',
    'jobApplication.fullNamePlaceholder': 'Enter your full name',
    'jobApplication.country': 'Country *',
    'jobApplication.countryPlaceholder': 'Enter your country',
    'jobApplication.email': 'Email *',
    'jobApplication.emailPlaceholder': 'Enter your email',
    'jobApplication.phone': 'Mobile Phone *',
    'jobApplication.phonePlaceholder': 'Enter your mobile number',
    'jobApplication.cvLabel': 'CV * (PDF or Word, max 5MB)',
    'jobApplication.cvUploadText': 'Click to upload your CV',
    'jobApplication.chooseFile': 'Choose File',
    'jobApplication.coverLetterLabel': 'Cover Letter (optional)',
    'jobApplication.coverLetterUploadText': 'Click to upload cover letter',
    'jobApplication.cancel': 'Cancel',
    'jobApplication.submit': 'Submit Application',
    'jobApplication.submitting': 'Submitting...',
    'jobApplication.fileTooLarge': 'File too large',
    'jobApplication.fileTooLargeDescription': 'Please upload a file smaller than 5MB.',
    'jobApplication.invalidFileType': 'Invalid file type',
    'jobApplication.invalidFileTypeDescription': 'Please upload a PDF or Word document.',
    'jobApplication.missingFields': 'Missing required fields',
    'jobApplication.missingFieldsDescription': 'Please fill in all required fields.',
    'jobApplication.cvRequired': 'CV required',
    'jobApplication.cvRequiredDescription': 'Please upload your CV.',
    'jobApplication.submissionFailed': 'Submission failed',
    'jobApplication.submissionFailedDescription': 'Please try again later.',
    'jobApplication.applicationSubmitted': 'Application submitted!',
    'applicationSubmittedDescription': 'We\'ll review your application and get back to you soon.',
    'jobApplication.partialSuccess': 'Application submitted',
    'jobApplication.partialSuccessDescription': 'Your application was saved but there was an issue sending the notification email.',
    
    // 404 Page
    'notFound.title': '404',
    'notFound.message': 'Oops! This page has vanished into the void',
    'notFound.returnHome': 'Return to Home',
  },
  hu: {
    // Navigation
    'nav.home': 'Főoldal',
    'nav.services': 'Szolgáltatások',
    'nav.about': 'Rólunk',
    'nav.contact': 'Munkáink',
    'nav.joinUs': 'Karrier',
    'nav.language': 'Nyelv',
    
    // Hero Section
    'hero.title': 'Formáld át a vállalkozásod',
    'hero.subtitle': 'Élvonalbeli technológiával',
    'hero.description': 'Innovatív AI‑megoldásokat, látványos webdesignt és stratégiai IT‑tanácsadást kínálunk, amelyek kézzelfogható üzleti eredményeket hoznak.',
    'hero.cta': 'Tedd meg az elsö lépést!',
    'hero.getStarted': 'Vágjunk bele',
    
    // Services Section
    'services.badge': 'Szolgáltatási portfólió',
    'services.title': 'Stratégia, fejlesztés, növekedés',
    'services.titleHighlight': ' ',
    'services.subtitle': 'Átfogó megoldások a digitális jelenlét, automatizálás és skálázás támogatására.',
    'services.aiTitle': 'AI‑megoldások',
    'services.aiDescription': 'Használd ki a mesterséges intelligencia erejét üzleted átalakítására – a gépi tanulási modellektől az intelligens automatizálásig.',
    'services.webTitle': 'Web + SEO',
    'services.webDescription': 'SEO‑ra hangolt weboldalak, amelyek nemcsak lenyűgöznek, hanem ügyfeleket is hoznak. Teljes körű megoldásunk gondoskodik róla, hogy online jelenléted ne csupán dísz legyen, hanem valódi konverziókat generáljon.',
    'services.itTitle': 'IT‑tanácsadás',
    'services.itDescription': 'Stratégiai technológiai útmutatás, amely összhangban van üzleti céljaiddal – az infrastruktúra tervezésétől, AI megoldásokon át a digitális átalakulási stratégiákig.',
    'services.softwareTitle': 'Szoftverfejlesztés',
    'services.softwareDescription': 'Egyedi szoftvermegoldások, amelyek a te egyedi üzleti igényeidhez igazodnak. A mobil alkalmazásoktól a vállalati rendszerekig skálázható megoldásokat építünk.',
    'services.socialTitle': 'Munkafolyamatok Automatizálása',
    'services.socialDescription': 'Cseréld le a monoton, manuális feladatokat automatizált megoldásokra, amelyek növelik a termelékenységet és több időt hagynak a kreatív munkára.',
    'services.learnMore': 'Tudj meg többet',
    'services.ctaTitle': 'Beszéljünk még',
    'services.ctaTitleHighlight': 'Ma!',
    'services.ctaText': 'Készen állsz vállalkozásod átalakítására? Elnyerték szolgáltatásaink a tetszésedet?',
    'services.ctaButton': 'Vedd fel velünk a kapcsolatot',
    
    // Features
    'features.customAI': 'Egyedi AI‑modellek',
    'features.processAutomation': 'Folyamat‑automatizálás',
    'features.chatbots': 'Intelligens chatbotok',
    'features.analytics': 'Prediktív analitika',
    'features.webDesign': 'Modern webdesign',
    'features.seoOptimization': 'SEO‑optimalizálás',
    'features.ecommerce': 'E‑kereskedelmi megoldások',
    'features.performance': 'Teljesítményoptimalizálás',
    'features.techStrategy': 'Technológiai stratégia',
    'features.infrastructure': 'Infrastruktúra‑tervezés',
    'features.security': 'Biztonsági tanácsadás',
    'features.aiConsulting': 'AI tanácsadás',
    'features.digitalTransformation': 'Digitális átalakulás',
    'features.customApps': 'Egyedi alkalmazások',
    'features.mobileDev': 'Mobil fejlesztés',
    'features.enterpriseSystems': 'Vállalati rendszerek',
    'features.apiIntegration': 'API‑integráció',
    'features.socialStrategy': 'Munkafolyamat automatizálás',
    'features.contentCreation': 'Feladatoptimalizálás',
    'features.adManagement': 'Integrációs platformok',
    'features.reporting': 'Folyamatmonitorozás',
    
    // Hero Section Additional
    'hero.companyName': 'commodIT',
    'hero.tagline': 'Az IT Úttörői',
    'hero.transformBusiness': 'Formáld át vállalkozásod élvonalbeli automatizálással, prémium webfejlesztéssel és szakértő IT‑tanácsadással.',
    'hero.freeConsultation': 'Ingyenes konzultáció',
    'hero.viewOurWork': 'Nézd meg munkáinkat',

    // About Section
    'about.whyChoose': 'Miért válaszd a',
    'about.commodIT': 'commodIT‑t',
    'about.notJustTech': 'Nem csak egy újabb techcég vagyunk – a digitális innováció stratégiai partnereként állunk melletted.',
    'about.yourSuccess': 'A sikered a küldetésünk',
    'about.buildingPartnerships': 'Nálunk a te víziód a mi víziónk. A projektjeidet sajátként kezeljük, így biztosítva a magas minőséget és az üzleti megtérülést.',
    'about.whatSetsUsApart': 'Ami megkülönböztet minket:',
    'about.yearsExperience': '5+ év tapasztalat IT‑tanácsadásban és fejlesztésben',
    'about.clientSatisfaction': '100% ügyfél‑elégedettség',
    'about.cuttingEdge': 'Legmodernebb technológiák alkalmazása',
    'about.multilingualSupport': 'Többnyelvű támogatás',
    'about.support247': '24/7 támogatás és karbantartás',
    'about.clientSatisfactionBadge': 'Ügyfél‑elégedettség',
    'about.ourValues': 'Értékeink',
    'about.valuesBullets': [
      'Átláthatóság és nyílt kommunikácó',
      'Egyszerű megoldások komplex problémákra',
      'IT biztonság és adatvédelem',
      'Tisztelet a határidők és költségvetések iránt',
      'Céltudatos innováció',
      'Az ügyfél sikere az első'
    ],
    'about.valuesMotto': 'Együtt építjük a holnapot',
    'about.valuesMottoDesc': 'Ahol a technológia találkozik a bizalommal',
    'about.expertTeam': 'Szakértő csapat',
    'about.expertTeamDesc': 'Tapasztalt szakemberek, mély iparági tudással',
    'about.provenResults': 'Bizonyított eredmények',
    'about.provenResultsDesc': 'Nyomon követhető sikerek digitális átalakulásokban',
    'about.agileDelivery': 'Agilis szállítás',
    'about.agileDeliveryDesc': 'Gyors, iteratív megközelítés, amely alkalmazkodik az igényeidhez',

    // Contact Section
    'contact.readyToGetStarted': 'Megtennéd az',
    'contact.getStarted': 'első lépést',
    'contact.discussProject': 'Beszéljünk az igényeidről, ötleteidről és fedezzük fel, hogyan segíthetünk.',
    'contact.letsConnect': 'Kapcsolódjunk',
    'contact.readyToTakeNext': 'Készen állsz, hogy vállalkozásod a következő szintre emeld? Vedd fel velünk a kapcsolatot egy ingyenes konzultációért. Felmérjük az igényeidet és személyre szabott ajánlásokat adunk.',
    'contact.email': 'E‑mail',
    'contact.phone': 'Telefon',
    'contact.location': 'Helyszín',
    'contact.responseTime': 'Válaszidő',
    'contact.lessThan24Hours': '< 24 óra',
    'contact.languages': 'Nyelvek',
    'contact.english': 'Angol',
    'contact.magyar': 'Magyar',
    'contact.emailValue': 'info@commodit.hu',
    'contact.phoneValue': '+36 30 117 6526',
    'contact.freeConsultation': 'Ingyenes konzultáció',
    'contact.bookDiscovery': 'Foglalj egy 30 perces felderítő hívást a projekted követelményeinek, kihívásainak és céljainak megbeszélésére. Nincs kötelezettség.',
    'contact.projectScope': 'Projekt hatókörének elemzése',
    'contact.techRecommendations': 'Technológiai ajánlások',
    'contact.timelineBudget': 'Ütemterv és költségbecslés',
    'contact.scheduleConsultation': 'Ingyenes konzultáció',
    'contact.usuallyResponds': 'Átlagos válasz ~2-4 órán belül',
    'contact.locationValue': 'Budapest, Magyarország',

    // References Section
    'references.our': 'Munkáink',
    'references.works': ' ',
    'references.portfolioDesc': 'Tekintsd meg sikersztoriainkat, amelyekre büszkék vagyunk!',
    'references.pauseAutoScroll': 'Automatikus görgetés szüneteltetése',
    'references.playAutoScroll': 'Automatikus görgetés indítása',
    'references.play': 'Lejátszás',
    'references.pause': 'Szünet',
    'references.tapToView': 'Érintsd meg a megtekintéshez',
    'references.view': 'Megtekintés',
    'references.ecommercePlatform': 'E‑kereskedelmi platform',
    'references.corporateWebsite': 'Vállalati weboldal',
    'references.logisticsDashboard': 'Logisztikai irányítópult',
    'references.fizgo': 'FIZGO',
    'references.innovationPlatform': 'Innovációs platform',
    'references.mobileApp': 'Mobilalkalmazás',
    'references.dataAnalytics': 'Adatelemzési platform',
    'references.aiSolutions': 'AI‑megoldások',
    'references.webDevelopmentSeo': 'Webfejlesztés és SEO',
    'references.softwareDevelopment': 'Szoftverfejlesztés',
    'references.softposAutomation': 'softPOS & Automation',
    'references.itConsulting': 'IT‑tanácsadás',

    // Join Us Section
    'joinus.joinUs': 'Csatlakozz hozzánk',
    'joinus.lookingForTalented': 'Tehetséges szabadúszókat keresünk növekvő csapatunkba. Fedezd fel az izgalmas lehetőségeket, és dolgozz innovatív projekteken.',
    'joinus.freelanceDeveloper': 'Freelancer Low‑Code Fejlesztő',
    'joinus.developerDesc': 'Építs innovatív webalkalmazásokat modern technológiákkal. Dolgozz izgalmas projekteken rugalmas ütemezésben.',
    'joinus.freelanceAITechnician': 'Freelancer AI Technikus',
    'joinus.aiTechnicianDesc': 'AI‑megoldások megvalósítása és karbantartása. Segíts ügyfeleknek kihasználni a mesterséges intelligenciát üzleti igényeikhez.',
    'joinus.freelanceSalesPerson': 'Freelancer Értékesítő',
    'joinus.salesPersonDesc': 'Segíts az üzleti növekedésben potenciális ügyfelekkel való kapcsolatfelvétellel. Képviseld élvonalbeli technológiai megoldásainkat.',
    'joinus.learnMore': 'Tudj meg többet',
    'joinus.learnMoreDeveloper': 'Tudj meg többet a fejlesztői pozícióról',
    'joinus.learnMoreAITechnician': 'Tudj meg többet az AI technikus pozícióról',
    'joinus.learnMoreSales': 'Tudj meg többet az értékesítői pozícióról',

    // Footer
    'footer.transformingBusinesses': 'Vállalkozásokat alakítunk át innovatív technológiai megoldásokkal és szakértői tanácsadással.',
    'footer.services': 'Szolgáltatások',
    'footer.navigation': 'Navigáció',
    'footer.legal': 'Jogi információk',
    'footer.privacyPolicy': 'Adatvédelmi irányelvek',
    'footer.downloadPdf': 'PDF letöltése',
    'footer.allRightsReserved': '© 2025 commodIT. Minden jog fenntartva!',
    'footer.aiSolutions': 'AI‑megoldások',
    'footer.webSeo': 'Web + SEO',
    'footer.itConsulting': 'IT‑tanácsadás',
    'footer.softwareDevelopment': 'Szoftverfejlesztés',
    'footer.about': 'Rólunk',
    'footer.references': 'Referenciák',
    'footer.contact': 'Kapcsolat',

    // Service Pages - AI Solutions
    'aiSolutions.title': 'AI‑megoldások',
    'aiSolutions.heroDescription': 'Spórolj időt és pénzt vállalkozásodnak az ismétlődő feladatok és munkafolyamatok automatizálásával. Az egyedi gépi tanulási modellektől az intelligens automatizálási rendszerekig olyan szolgáltatásokat kínálunk, amelyek forradalmasítják a munkafolyamataidat.',
    'aiSolutions.startJourney': 'Beszéljünk még ma!',
    'aiSolutions.whatWeDeliver': 'Amit nyújtunk',
    'aiSolutions.customAIModels': 'Egyedi AI‑modellek',
    'aiSolutions.customAIDescription': 'Személyre szabott gépi tanulási megoldások, amelyek kifejezetten a te üzleti igényeidhez és adatmintáidhoz készülnek.',
    'aiSolutions.processAutomation': 'Folyamat‑automatizálás',
    'aiSolutions.processAutomationDescription': 'Egyszerűsítjük az ismétlődő feladatokat és munkafolyamatokat intelligens automatizálással, amely tanul és alkalmazkodik az igényeidhez.',
    'aiSolutions.intelligentChatbots': 'Intelligens chatbotok',
    'aiSolutions.intelligentChatbotsDescription': 'Fejlett AI‑asszisztensek, amelyek 24/7 ügyfélszolgálatot biztosítanak természetes nyelvű szövegértéssel, szóban és írásban.',
    'aiSolutions.predictiveAnalytics': 'Prediktív analitika',
    'aiSolutions.predictiveAnalyticsDescription': 'Adatelemzés a jövőbeli trendek előrejelzésére és a döntések megalapozására.',
    'aiSolutions.ourAIProcess': 'Hogyan valósítjuk meg?',
    'aiSolutions.dataAnalysis': 'Adatelemzés',
    'aiSolutions.dataAnalysisDescription': 'Elemezzük az adataidat és igényeidet, hogy azonosítsuk az AI lehetőségeket.',
    'aiSolutions.modelDevelopment': 'Modellfejlesztés',
    'aiSolutions.modelDevelopmentDescription': 'Egyedi AI‑modellek fejlesztése és betanítása kifejezetten a te igényeidre szabva.',
    'aiSolutions.integrationOptimization': 'Integráció és optimalizálás',
    'aiSolutions.integrationOptimizationDescription': 'Zökkenőmentes integráció a meglévő rendszereidbe, folyamatos optimalizálással és támogatással.',
    'aiSolutions.readyToHarness': 'Készen állsz kiaknázni az AI erejét?',
    'aiSolutions.readyToHarnessDescription': 'Tudd meg milyen előnyökkel járhat vállalkozásod számára az AI. Vedd fel velünk a kapcsolatot egy ingyenes konzultációért, és tedd meg az első lépést a növekedés felé!',
    'aiSolutions.getFreeConsultation': 'Ingyenes AI‑konzultáció',

    // Service Pages - IT Consulting
    'itConsulting.title': 'IT‑tanácsadás',
    'itConsulting.heroDescription': 'Stratégiai technológiai útmutatást adunk, amely átalakítja az üzleted működését. Az infrastruktúra megtervezésétől a digitalizálásig szakértői tanácsadást nyújtunk, amely valódi eredményeket hoz.',
    'itConsulting.strategicGuidance': 'Beszéljünk még ma',
    'itConsulting.ourExpertise': 'Amiben segítünk',
    'itConsulting.technologyStrategy': 'Technológiai stratégia',
    'itConsulting.technologyStrategyDescription': 'Átfogó technológiai stratégiák, amelyek összhangban vannak üzleti céljaiddal és fenntartható növekedést biztosítanak.',
    'itConsulting.infrastructurePlanning': 'Infrastruktúra‑tervezés',
    'itConsulting.infrastructurePlanningDescription': 'Skálázható IT‑infrastruktúra tervezése, amely együtt nő vállalkozásoddal és optimalizálja a hatékonyságod.',
    'itConsulting.securityConsulting': 'Biztonsági tanácsadás',
    'itConsulting.securityConsultingDescription': 'Kiberbiztonsági stratégiák kidolgozása digitális eszközeid védelmére.',
    'itConsulting.digitalTransformation': 'Digitális átalakulás',
    'itConsulting.digitalTransformationDescription': 'Üzleted digitalizálása a működés modernizálása és versenyképességed fokozása érdekében.',
    'itConsulting.consultingAreas': 'Tanácsadási területek',
    'itConsulting.technologyAssessment': 'Technológiai felmérés',
    'itConsulting.currentStateAnalysis': 'Jelenlegi állapot elemzése',
    'itConsulting.gapIdentification': 'Hiányosságok azonosítása',
    'itConsulting.technologyAudit': 'Technológiai audit',
    'itConsulting.performanceReview': 'Teljesítményértékelés',
    'itConsulting.strategicPlanning': 'Stratégiai tervezés',
    'itConsulting.itRoadmapCreation': 'IT‑stratégia készítés',
    'itConsulting.budgetPlanning': 'Költségvetés‑tervezés',
    'itConsulting.riskManagement': 'Kockázatkezelés',
    'itConsulting.roiAnalysis': 'ROI‑elemzés',
    'itConsulting.implementationSupport': 'Megvalósítási támogatás',
    'itConsulting.projectManagement': 'Projektmenedzsment',
    'itConsulting.changeManagement': 'Változásmenedzsment',
    'itConsulting.trainingPrograms': 'Képzési programok',
    'itConsulting.qualityAssurance': 'Minőségbiztosítás',
    'itConsulting.aiConsulting': 'AI‑tanácsadás',
    'itConsulting.aiStrategyDevelopment': 'AI‑stratégia kidolgozása',
    'itConsulting.aiToolSelection': 'AI‑eszköz kiválasztás és integráció',
    'itConsulting.aiWorkflowOptimization': 'AI‑munkafolyamat optimalizálás',
    'itConsulting.aiPerformanceMonitoring': 'AI‑teljesítmény monitorozás',
    'itConsulting.transformTechnologyStrategy': 'Alakítsd át technológiai stratégiád',
    'itConsulting.transformTechnologyStrategyDescription': 'Tervezzük meg közösen a jövődet. Vedd fel velünk a kapcsolatot egy ingyenes konzultációért, és tedd meg az első lépést a digitális átalakulás felé.',
    'itConsulting.scheduleStrategySession': 'Foglalj időpontot',
    
    // Service Pages - Software Development
    'softwareDevelopment.title': 'Szoftverfejlesztés',
    'softwareDevelopment.heroDescription': 'Egyedi szoftvermegoldások, amelyek a te üzleti igényeidhez igazodnak. A mobilalkalmazásoktól a vállalati rendszerekig skálázható, megbízható szoftvereket építünk, hogy segítsük a vállalkozásod.',
    'softwareDevelopment.startProject': 'Kezdd el a projekted',
    'softwareDevelopment.whatWeBuild': 'Mit fejlesztünk?',
    'softwareDevelopment.customApplications': 'Egyedi alkalmazások',
    'softwareDevelopment.customApplicationsDescription': 'Személyre szabott szoftvermegoldások, amelyek igazodnak az egyedi üzleti kihívásaidhoz és megfelelnek a követelményeidnek.',
    'softwareDevelopment.mobileDevelopment': 'Mobil fejlesztés',
    'softwareDevelopment.mobileDevelopmentDescription': 'Natív és cross‑platform mobilalkalmazások iOS és Android rendszerekre.',
    'softwareDevelopment.enterpriseSystems': 'Vállalati rendszerek',
    'softwareDevelopment.enterpriseSystemsDescription': 'Skálázható vállalati szintű szoftverrendszerek, amelyek zökkenőmentesen integrálhatók a meglévő infrastruktúrádba.',
    'softwareDevelopment.apiIntegration': 'API‑integráció',
    'softwareDevelopment.apiIntegrationDescription': 'Zökkenőmentes integrációs szolgáltatások, amelyek összekapcsolják a rendszereidet és leegyszerűsítik az adatáramlást a platformok között.',
    'softwareDevelopment.ourTechnologyStack': 'Technológiai stackünk',
    'softwareDevelopment.frontend': 'Frontend',
    'softwareDevelopment.backend': 'Backend',
    'softwareDevelopment.mobileCloud': 'Mobil és felhő',
    'softwareDevelopment.developmentProcess': 'Fejlesztési folyamat',
    'softwareDevelopment.discovery': 'Felderítés',
    'softwareDevelopment.discoveryDescription': 'A követelmények összegyűjtése és technikai elemzése',
    'softwareDevelopment.design': 'Tervezés',
    'softwareDevelopment.designDescription': 'UI/UX tervezés és rendszerarchitektúra',
    'softwareDevelopment.development': 'Fejlesztés',
    'softwareDevelopment.developmentDescription': 'Agilis fejlesztés rendszeres frissítésekkel',
    'softwareDevelopment.testing': 'Tesztelés',
    'softwareDevelopment.testingDescription': 'Átfogó tesztelés és minőségbiztosítás',
    'softwareDevelopment.deployment': 'Telepítés',
    'softwareDevelopment.deploymentDescription': 'A szoftver átadása folyamatos karbantartási támogatással',
    'softwareDevelopment.readyToBuild': 'Készen állsz valami újat építeni?',
    'softwareDevelopment.readyToBuildDescription': 'Beszéljünk a projektedről, és derítsük ki, hogyan segíthetünk egyedi megoldásainkkal.',
    'softwareDevelopment.discussProject': 'Felveszem a kapcsolatot',

    // Service Pages - Automate Workflows
    'automateWorkflows.title': 'Munkafolyamatok automatizálása',
    'automateWorkflows.heroDescription': 'Egyszerűsítsd az üzleti folyamataidat intelligens automatizálási megoldásokkal, amelyek kiváltják a manuális feladatokat, csökkentik a hibákat és növelik a vállalatod termelékenységét.',
    'automateWorkflows.startAutomating': 'Keress minket!',
    'automateWorkflows.ourServices': 'Automatizálási szolgáltatásaink',
    'automateWorkflows.workflowAutomation': 'Munkafolyamat‑automatizálás',
    'automateWorkflows.workflowAutomationDescription': 'Automatizált munkafolyamatok tervezése és megvalósítása, amelyek kiiktatják az ismétlődő manuális feladatokat és csökkentik az emberi hibákat.',
    'automateWorkflows.taskOptimization': 'Feladatoptimalizálás',
    'automateWorkflows.taskOptimizationDescription': 'Feladatsorozatok optimalizálása a hatékonyság maximalizálása és a feldolgozási idő minimalizálása érdekében.',
    'automateWorkflows.integrationPlatforms': 'Integrációs platformok',
    'automateWorkflows.integrationPlatformsDescription': 'Különböző üzleti rendszerek összekapcsolása és integrálása zökkenőmentes, automatizált folyamatok létrehozásához.',
    'automateWorkflows.processMonitoring': 'Folyamatmonitorozás',
    'automateWorkflows.processMonitoringDescription': 'Valós idejű monitorozás és elemzés a munkafolyamatok teljesítményének nyomon követésére és az optimalizálási lehetőségek azonosítására.',
    'automateWorkflows.businessAreasTitle': 'Üzleti területek, amelyeket automatizálunk',
    'automateWorkflows.customerService': 'Ügyfélszolgálat',
    'automateWorkflows.customerServiceDesc': 'Ticketing‑rendszerek automatizálása',
    'automateWorkflows.dataProcessing': 'Adatfeldolgozás',
    'automateWorkflows.dataProcessingDesc': 'Automatizált adatbevitel',
    'automateWorkflows.financeAccounting': 'Pénzügy és könyvelés',
    'automateWorkflows.financeAccountingDesc': 'Számlák feldolgozása és készítése',
    'automateWorkflows.marketing': 'Marketing',
    'automateWorkflows.marketingDesc': 'Leadek minősítése',
    'automateWorkflows.hrOperations': 'HR',
    'automateWorkflows.hrOperationsDesc': 'Automatikus onboarding rendszer fejlesztése',
    'automateWorkflows.inventoryManagement': 'Készletkezelés',
    'automateWorkflows.inventoryManagementDesc': 'Készletek monitorozása',
    'automateWorkflows.qualityAssurance': 'Minőségbiztosítás',
    'automateWorkflows.qualityAssuranceDesc': 'Tesztelés automatizálása',
    'automateWorkflows.communications': 'Kommunikáció',
    'automateWorkflows.communicationsDesc': 'Hideghívó rendszerek kialakítása',
    'automateWorkflows.automationBenefits': 'Az automatizálás előnyei',
    'automateWorkflows.increasedEfficiency': 'Hatékonyság növelése',
    'automateWorkflows.increasedEfficiencyDescription': 'Automatizáld az ismétlődő feladatokat, hogy a csapatod stratégiai munkára fordíthassa a megspórolt időt.',
    'automateWorkflows.errorReduction': 'Hibák csökkentése',
    'automateWorkflows.errorReductionDescription': 'Szüntesd meg az emberi hibákat a rutinfolyamatokban, és biztosíts következetes, pontos eredményeket minden alkalommal.',
    'automateWorkflows.costSavings': 'Költségmegtakarítás',
    'automateWorkflows.costSavingsDescription': 'Csökkentsd a működési költségeidet automatizálással, miközben javítod szolgáltatásod minőségét és az ügyfél‑elégedettséget.',
    'automateWorkflows.twentyFourSevenOperation': '24/7 működés',
    'automateWorkflows.fasterProcessing': 'Gyorsabb feldolgozás',
    'automateWorkflows.resourceOptimization': 'Erőforrás‑optimalizálás',
    'automateWorkflows.scalableSolutions': 'Skálázható megoldások',
    'automateWorkflows.consistentOutput': 'Következetes kimenet',
    'automateWorkflows.dataAccuracy': 'Adatpontosság',
    'automateWorkflows.complianceAssurance': 'Megfelelőségi garancia',
    'automateWorkflows.qualityControl': 'Minőség‑ellenőrzés',
    'automateWorkflows.lowerLaborCosts': 'Alacsonyabb munkaerőköltségek',
    'automateWorkflows.reducedErrors': 'Csökkentett hibák',
    'automateWorkflows.fasterTurnaround': 'Gyorsabb átfutási idő',
    'automateWorkflows.betterResourceAllocation': 'Jobb erőforrás‑elosztás',
    'automateWorkflows.readyToAutomate': 'Készen állsz vállalkozásod automatizálására?',
    'automateWorkflows.readyToAutomateDescription': 'Alakítsd át a működésedet automatizálási megoldásokkal, amelyek éjjel‑nappal a produktivitásod és a hatékonyságod növelésén dolgoznak.',
    'automateWorkflows.startAutomationJourney': 'Kezdd el automatizálni a munkádat',

    // Service Pages - Web & SEO
    'webSeo.title': 'Web és SEO',
    'webSeo.heroDescription': 'Modern, nagy teljesítményű webhelyeket készítünk, amelyek képesek felvenni a versenyt a keresőtalálokban elért helyezésekért. Munkánkkal biztosítjuk, hogy az online jelenléted valódi üzleti növekedést és mérhető eredményeket hozzon.',
    'webSeo.launchWebsite': 'Vágjunk bele!',
    'webSeo.whatWeCreate': 'Amit kínálunk',
    'webSeo.modernWebDesign': 'Modern webdesign',
    'webSeo.modernWebDesignDescription': 'Profi, reszponzív webhelyek, amelyek a legújabb technológiákkal és tervezési elvekkel készülnek, és megragadják a látogatóid figyelmét.',
    'webSeo.seoOptimization': 'SEO‑optimalizálás',
    'webSeo.seoOptimizationDescription': 'Stratégiai keresőmotor‑optimalizálás, amely a webhelyedet a keresési eredmények élére emeli és organikus forgalmat generál.',
    'webSeo.ecommerceSolutions': 'E‑kereskedelmi megoldások',
    'webSeo.ecommerceSolutionsDescription': 'Teljes online áruházak biztonságos fizetéskezeléssel, készletkezeléssel és konverzióoptimalizálással.',
    'webSeo.performanceOptimization': 'Teljesítményoptimalizálás',
    'webSeo.performanceOptimizationDescription': 'Villámgyors betöltési sebesség és optimális felhasználói élmény, amely megtartja a látogatóidat és ügyfelekké konvertálja őket.',
    'webSeo.ourWebProcess': 'Webkészítési folyamatunk',
    'webSeo.strategyPlanning': 'Stratégia és tervezés',
    'webSeo.strategyPlanningDescription': 'Elemezzük a versenytársaidat és a piacodat, és átfogó stratégiát készítünk.',
    'webSeo.designDevelopment': 'Tervezés és fejlesztés',
    'webSeo.designDevelopmentDescription': 'Modern, reszponzív tervek kelnek életre korszerű technológiával.',
    'webSeo.seoImplementation': 'SEO‑megvalósítás',
    'webSeo.seoImplementationDescription': 'Stratégiai optimalizálás a maximális keresőmotor‑láthatóság érdekében.',
    'webSeo.launchGrowth': 'Indítás és növekedés',
    'webSeo.launchGrowthDescription': 'Telepítés és folyamatos optimalizálás.',
    'webSeo.readyToDominate': 'Készen állsz, hogy egy professzionális weboldal tulajdonosa legyél?',
    'webSeo.readyToDominateDescription': 'Hozzunk létre egy webhelyet, amely nemcsak szép, hanem forgalmat is hoz, és a látogatókat ügyfelekké alakítja.',
    'webSeo.startWebProject': 'Csináljuk!',

    // Job Pages - Common
    'jobs.backToHome': 'Vissza a főoldalra',
    'jobs.applyNow': 'Jelentkezz most',
    'jobs.jobDescription': 'Állásleírás',
    'jobs.keyResponsibilities': 'Főbb feladatok:',
    'jobs.requiredQualifications': 'Szükséges képesítések',
    'jobs.technicalSkills': 'Technikai készségek:',
    'jobs.skills': 'Készségek:',
    'jobs.experience': 'Tapasztalat:',
    'jobs.whatWeOffer': 'Mit kínálunk',
    'jobs.applyForPosition': 'Jelentkezés erre a pozícióra',

    // Job Pages - Freelance Developer
    'freelanceDeveloper.title': 'Freelancer low‑code fejlesztő',
    'freelanceDeveloper.heroDescription': 'Csatlakozz a csapatunkhoz, és építs innovatív webalkalmazásokat a legújabb technológiákkal.',
    'freelanceDeveloper.jobDescriptionText': 'Tehetséges szabadúszó fejlesztőket keresünk növekvő csapatunkba. Izgalmas projekteken dolgozhatsz különböző ügyfelekkel, modern webalkalmazásokat építve.',
    'freelanceDeveloper.responsibility1': 'Reszponzív oldalak fejlesztése Reacttel és TypeScript‑tel',
    'freelanceDeveloper.responsibility2': 'Felhasználóbarát UI‑k készítése',
    'freelanceDeveloper.responsibility3': 'Tiszta, karbantartható és jól dokumentált modulok írása',
    'freelanceDeveloper.responsibility4': 'Részvétel kódreviewkban és technikai megbeszéléseken',
    'freelanceDeveloper.responsibility5': 'Munka modern fejlesztői eszközökkel és munkafolyamatokkal',
    'freelanceDeveloper.experience1': 'Webfejlesztési tapasztalat',
    'freelanceDeveloper.experience2': 'Portfólió befejezett projektekről',
    'freelanceDeveloper.experience3': 'Tapasztalat modern automatizációs munkafolyamatokkal (pl. N8N)',
    'freelanceDeveloper.experience4': 'Reszponzív tervezési elvek ismerete',
    'freelanceDeveloper.competitiveRates': 'Versenyképes díjazás',
    'freelanceDeveloper.competitiveRatesDescription': 'Vonzó óradíjak a tapasztalat és a projekt összetettsége alapján',
    'freelanceDeveloper.flexibleSchedule': 'Rugalmas beosztás',
    'freelanceDeveloper.flexibleScheduleDescription': 'Munka saját időbeosztás szerint rugalmas határidőkkel',
    'freelanceDeveloper.greatTeam': 'Nagyszerű csapat',
    'freelanceDeveloper.greatTeamDescription': 'Együttműködés tapasztalt szakemberekkel és fejlődési lehetőségek a projekteken',
    'freelanceDeveloper.modernTech': 'Modern technológiák',
    'freelanceDeveloper.modernTechDescription': 'Munka a legújabb technológiákkal',
    'freelanceDeveloper.readyToJoin': 'Szeretnél csatlakozni a csapatunkhoz?',
    'freelanceDeveloper.readyToJoinDescription': 'Küldd el a jelentkezésed még ma, és építs csodálatos projekteket velünk. Minden jelentkezést gondosan áttekintünk, és 48 órán belül válaszolunk.',

    // Job Pages - Freelance AI Technician
    'freelanceAITechnician.title': 'Freelancer AI szakértő',
    'freelanceAITechnician.heroDescription': 'Segíts ügyfeleinknek a legújabb AI‑megoldások bevezetésében.',
    'freelanceAITechnician.jobDescriptionText': 'Csatlakozz a csapatunkhoz AI‑szakértőként, és segíts vállalkozásoknak modernizálni a működésüket. AI‑megoldásokat valósítasz meg, tartasz karban és optimalizálsz különböző ügyfelek számára.',
    'freelanceAITechnician.responsibility1': 'AI‑modellek és megoldások megvalósítása és bevezetése',
    'freelanceAITechnician.responsibility2': 'API‑k és szolgáltatások integrálása ügyfélalkalmazásokba',
    'freelanceAITechnician.responsibility3': 'AI‑munkafolyamatok optimalizálása a teljesítmény és a pontosság érdekében',
    'freelanceAITechnician.responsibility4': 'Technikai támogatás és hibaelhárítás nyújtása',
    'freelanceAITechnician.responsibility5': 'AI‑megoldások és best practicek dokumentálása',
    'freelanceAITechnician.experience1': 'Tapasztalat AI/ML‑projektekben',
    'freelanceAITechnician.experience2': 'Tapasztalat AI‑agentek kezelésében',
    'freelanceAITechnician.experience3': 'Felhőalapú AI‑szolgáltatások ismerete (AWS, Azure, GCP)',
    'freelanceAITechnician.experience4': 'Tapasztalat adatfeldolgozásban és elemzésben',
    'freelanceAITechnician.premiumRates': 'Prémium díjazás',
    'freelanceAITechnician.premiumRatesDescription': 'Izgalmas projektek versenyképes kompenzációval',
    'freelanceAITechnician.flexibleWork': 'Rugalmas munka',
    'freelanceAITechnician.flexibleWorkDescription': 'Távmunka‑központú megközelítés projektalapú ütemezéssel',
    'freelanceAITechnician.expertNetwork': 'Szakértői hálózat',
    'freelanceAITechnician.expertNetworkDescription': 'Kapcsolat vezető AI‑szakemberekkel és kutatókkal',
    'freelanceAITechnician.cuttingEdgeAI': 'Legújabb AI‑technológiák',
    'freelanceAITechnician.cuttingEdgeAIDescription': 'Munka a legújabb AI‑technológiákkal és keretrendszerekkel',
    'freelanceAITechnician.readyToShape': 'Készen állsz a jövő formálására?',
    'freelanceAITechnician.readyToShapeDescription': 'Csatlakozz az AI‑csapatunkhoz, és segíts vállalkozásoknak kihasználni a mesterséges intelligenciában rejlő potenciált. Jelentkezz ma, és légy a csapatunk része.',

    // Job Pages - Freelance Sales Person
    'freelanceSalesPerson.title': 'Freelancer értékesítő',
    'freelanceSalesPerson.heroDescription': 'Gyarapítsd az ügyfélkörünket potenciális ügyfelekkel való kapcsolatfelvétellel és a megoldásaink képviseletével.',
    'freelanceSalesPerson.jobDescriptionText': 'Csatlakozz az értékesítési csapatunkhoz, és segíts a vállalkozásoknak felfedezni, hogyan alakíthatják át a működésüket a megoldásainkkal. Te képviseled majd a cégünket és építesz kapcsolatokat.',
    'freelanceSalesPerson.responsibility1': 'Potenciális ügyfelek és lehetőségek azonosítása és értékelése',
    'freelanceSalesPerson.responsibility2': 'Szolgáltatásaink és megoldásaink bemutatása potenciális ügyfeleknek',
    'freelanceSalesPerson.responsibility3': 'Erős ügyfélkapcsolatok építése és fenntartása',
    'freelanceSalesPerson.responsibility4': 'Szerződések tárgyalása és ügyletek lezárása',
    'freelanceSalesPerson.responsibility5': 'Együttműködés technikai csapatokkal',
    'freelanceSalesPerson.experience1': 'B2B és/vagy B2C értékesítési tapasztalat',
    'freelanceSalesPerson.experience2': 'Tapasztalat technológiai megoldások értékesítésében',
    'freelanceSalesPerson.experience3': 'Korábbi projektjeid bemutatása',
    'freelanceSalesPerson.experience4': 'Erős kommunikációs és prezentációs készségek',
    'freelanceSalesPerson.commissionStructure': 'Jutalékstruktúra',
    'freelanceSalesPerson.commissionStructureDescription': 'Teljesítményalapú jutalékok',
    'freelanceSalesPerson.flexibleSchedule': 'Rugalmas beosztás',
    'freelanceSalesPerson.flexibleScheduleDescription': 'Távmunka rugalmas órákkal',
    'freelanceSalesPerson.supportTeam': 'Támogató csapat',
    'freelanceSalesPerson.supportTeamDescription': 'Munka tapasztalt fejlesztői csapatokkal',
    'freelanceSalesPerson.growthOpportunities': 'Növekedési lehetőségek',
    'freelanceSalesPerson.growthOpportunitiesDescription': 'Karriered fejlesztése egy gyorsan növekvő IT‑cégnél',
    'freelanceSalesPerson.readyToDriveGrowth': 'Készen állsz velünk dolgozni?',
    'freelanceSalesPerson.readyToDriveGrowthDescription': 'Csatlakozz az értékesítési csapatunkhoz, és segíts vállalkozásoknak átalakulni a megoldásainkkal. Jelentkezz ma, és kezdd el építeni a sikertörténeted velünk.',

    // Common
    'common.consultation': 'Ingyenes konzultáció',
    'common.close': 'Bezárás',
    'common.submit': 'Küldés',
    'common.loading': 'Betöltés…',
    
    // Consultation Modal
    'consultation.title': 'Ingyenes konzultáció kérése',
    'consultation.description': 'Mondd el, hogyan segíthetünk, miről szeretnél beszélni. 24 órán belül válaszolunk.',
    'consultation.name': 'Név *',
    'consultation.namePlaceholder': 'Teljes neved',
    'consultation.profession': 'Szakma',
    'consultation.professionPlaceholder': 'Jelenlegi szereped',
    'consultation.age': 'Életkor',
    'consultation.agePlaceholder': 'Életkorod',
    'consultation.industry': 'Iparág',
    'consultation.industryPlaceholder': 'Válaszd ki az iparágad',
    'consultation.industryTechnology': 'Technológia',
    'consultation.industryHealthcare': 'Egészségügy',
    'consultation.industryFinance': 'Pénzügy',
    'consultation.industryEducation': 'Oktatás',
    'consultation.industryRetail': 'Kiskereskedelem',
    'consultation.industryManufacturing': 'Gyártás',
    'consultation.industryConsulting': 'Tanácsadás',
    'consultation.industryStartup': 'Startup',
    'consultation.industryOther': 'Egyéb',
    'consultation.email': 'E‑mail *',
    'consultation.emailPlaceholder': 'az.email@pelda.com',
    'consultation.phone': 'Telefonszám',
    'consultation.phonePlaceholder': '+36 (30) 123-4567',
    'consultation.descriptionLabel': 'Miről szeretnél beszélni? *',
    'consultation.descriptionPlaceholder': 'Írd le a projektedet, a kihívásaidat vagy amiről konzultálni szeretnél…',
    'consultation.cancel': 'Mégse',
    'consultation.submit': 'Küldés',
    'consultation.submitting': 'Küldés folyamatban...',
    'consultation.acceptPrivacy': 'Elfogadom az',
    'consultation.privacyPolicy': 'adatkezelési tájékoztatót',
    'consultation.error': 'Hiba',
    'consultation.errorDescription': 'A konzultációs kérés küldése sikertelen. Próbáld újra.',
    
    // Job Application Modal
    'jobApplication.title': 'Jelentkezés: {position}',
    'jobApplication.fullName': 'Teljes név *',
    'jobApplication.fullNamePlaceholder': 'Add meg a teljes neved',
    'jobApplication.country': 'Ország *',
    'jobApplication.countryPlaceholder': 'Add meg az országod',
    'jobApplication.email': 'E‑mail *',
    'jobApplication.emailPlaceholder': 'Add meg az e-mail címed',
    'jobApplication.phone': 'Mobil telefon *',
    'jobApplication.phonePlaceholder': 'Add meg a mobil számod',
    'jobApplication.cvLabel': 'Önéletrajz * (PDF vagy Word, max. 5 MB)',
    'jobApplication.cvUploadText': 'Kattints az önéletrajz feltöltéséhez',
    'jobApplication.chooseFile': 'Fájl kiválasztása',
    'jobApplication.coverLetterLabel': 'Motivációs levél (opcionális)',
    'jobApplication.coverLetterUploadText': 'Kattints a motivációs levél feltöltéséhez',
    'jobApplication.cancel': 'Mégse',
    'jobApplication.submit': 'Jelentkezés küldése',
    'jobApplication.submitting': 'Küldés…',
    'jobApplication.fileTooLarge': 'A fájl túl nagy',
    'jobApplication.fileTooLargeDescription': 'Kérjük, tölts fel 5 MB-nál kisebb fájlt.',
    'jobApplication.invalidFileType': 'Érvénytelen fájltípus',
    'jobApplication.invalidFileTypeDescription': 'Kérjük, PDF- vagy Word-dokumentumot tölts fel.',
    'jobApplication.missingFields': 'Hiányzó kötelező mezők',
    'jobApplication.missingFieldsDescription': 'Kérjük, tölts ki minden kötelező mezőt.',
    'jobApplication.cvRequired': 'Önéletrajz szükséges',
    'jobApplication.cvRequiredDescription': 'Kérjük, töltsd fel az önéletrajzodat.',
    'jobApplication.submissionFailed': 'A küldés sikertelen',
    'jobApplication.submissionFailedDescription': 'Próbáld újra később.',
    'jobApplication.applicationSubmitted': 'A jelentkezés elküldve!',
    'applicationSubmittedDescription': 'Köszönjük a jelentkezésedet, hamarosan felvesszük veled a kapcsolatot.',
    'jobApplication.partialSuccess': 'A jelentkezés elküldve',
    'jobApplication.partialSuccessDescription': 'Jelentkezésedet mentettük, de probléma volt az értesítő e‑mail küldésével.',
    
    // 404 Page
    'notFound.title': '404',
    'notFound.message': 'Hoppá! Ez az oldal eltűnt az űrben',
    'notFound.returnHome': 'Vissza a főoldalra',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('language');
    return (stored as Language) || 'en';
  });
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const updateTranslation = (key: string, value: string) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  };

  const t = (key: string): string => {
    if (overrides[key] !== undefined) return overrides[key];
    return translations[language][key] || key;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Load overrides for current language
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from('cms_translations')
        .select('key,value')
        .eq('language', language);
      if (!cancelled && !error) {
        const map: Record<string, string> = {};
        (data || []).forEach((row: any) => { map[row.key] = row.value; });
        setOverrides(map);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, updateTranslation }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};